import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
	Box,
	Flex,
	Text,
	Container,
	Heading,
	VStack,
	HStack,
} from '@chakra-ui/react';

const SECTIONS = [
	{ id: 'acceptance', title: '1. Acceptance of Terms' },
	{ id: 'description', title: '2. Description of Service' },
	{ id: 'accounts', title: '3. User Accounts' },
	{ id: 'use-rules', title: '4. Acceptable Use' },
	{ id: 'event-data', title: '5. Event Data & Content' },
	{ id: 'ip', title: '6. Intellectual Property' },
	{ id: 'payments', title: '7. Payments & Subscriptions' },
	{ id: 'termination', title: '8. Termination' },
	{ id: 'disclaimers', title: '9. Disclaimers' },
	{ id: 'limitation', title: '10. Limitation of Liability' },
	{ id: 'indemnification', title: '11. Indemnification' },
	{ id: 'governing-law', title: '12. Governing Law' },
	{ id: 'changes', title: '13. Changes to Terms' },
	{ id: 'contact', title: '14. Contact Us' },
];

const SectionHeading = ({ id, children }) => (
	<Heading
		id={id}
		as="h2"
		fontSize={{ base: '2xl', md: '28px' }}
		fontFamily="'Poppins', sans-serif"
		fontWeight="700"
		color="#111113"
		mt={{ base: 10, md: 14 }}
		mb={{ base: 3, md: 4 }}
		scrollMarginTop="100px"
	>
		{children}
	</Heading>
);

const Para = ({ children, ...props }) => (
	<Text
		fontSize={{ base: 'md', md: 'lg' }}
		color="#4B5563"
		lineHeight="1.75"
		mb={4}
		{...props}
	>
		{children}
	</Text>
);

const BulletList = ({ items }) => (
	<Box as="ul" pl={6} mb={4}>
		{items.map((item, i) => (
			<Box as="li" key={i} mb={2}>
				<Text fontSize={{ base: 'md', md: 'lg' }} color="#4B5563" lineHeight="1.75">
					{item}
				</Text>
			</Box>
		))}
	</Box>
);


// The article body on its own, so the signup terms modal shows exactly the
// same text as /terms without duplicating it. TermsPage below wraps this in
// the page chrome (hero, sidebar TOC, footer).
export const TermsContent = () => (
	<>
						<Para>
							These Terms of Service ("Terms") are an agreement between you and Stratospia Limited (RC 9526323), a company registered in Nigeria ("Stratospia", "we", "our", or "us"), which operates Eventory. They govern your access to and use of the Eventory platform, including our website, APIs, and related services (collectively, the "Service"). By creating an account or using the Service, you agree to these Terms. If you do not agree, do not use Eventory.
						</Para>

						<SectionHeading id="acceptance">1. Acceptance of Terms</SectionHeading>
						<Para>
							By accessing or using Eventory, you confirm that you are at least 16 years of age and have the legal capacity to enter into a binding agreement. If you are using Eventory on behalf of an organization, you represent that you have authority to bind that organization to these Terms.
						</Para>

						<SectionHeading id="description">2. Description of Service</SectionHeading>
						<Para>
							Eventory is an event management platform that enables users to plan, coordinate, and execute private and corporate events. Features include real-time dashboard management, guest tracking, kitchen coordination, table assignments, menu management, and team collaboration tools.
						</Para>
						<Para>
							We reserve the right to modify, suspend, or discontinue any part of the Service at any time, with or without notice. We will make reasonable efforts to notify you of material changes.
						</Para>

						<SectionHeading id="accounts">3. User Accounts</SectionHeading>
						<BulletList items={[
							'You must provide accurate, current, and complete registration information.',
							'You are responsible for maintaining the confidentiality of your account credentials.',
							'You are responsible for all activities that occur under your account.',
							'You must notify us immediately of any unauthorized access or security breach.',
							'We reserve the right to suspend or terminate accounts that violate these Terms.',
						]} />

						<SectionHeading id="use-rules">4. Acceptable Use</SectionHeading>
						<Para>You agree not to:</Para>
						<BulletList items={[
							'Use the Service for any unlawful purpose or to violate any applicable laws or regulations.',
							'Upload, transmit, or distribute harmful content including malware, spam, or phishing material.',
							'Attempt to gain unauthorized access to the Service, other user accounts, or related systems.',
							'Interfere with or disrupt the integrity or performance of the Service.',
							'Scrape, crawl, or use automated means to access the Service without our written consent.',
							'Impersonate another person or entity, or falsely represent your affiliation.',
							'Use the Service to collect personal information about others without their consent.',
						]} />

						<SectionHeading id="event-data">5. Event Data & Content</SectionHeading>
						<Para>
							You retain ownership of any content, data, and materials you upload to or create on Eventory ("Your Content"). By using the Service, you grant us a limited, non-exclusive license to host, store, and process Your Content solely to provide and improve the Service.
						</Para>
						<Para>
							You are solely responsible for Your Content and for ensuring it complies with applicable laws. We do not endorse or assume liability for any user-generated content.
						</Para>

						<SectionHeading id="ip">6. Intellectual Property</SectionHeading>
						<Para>
							The Eventory name, logo, website design, software, and all related intellectual property are owned by Eventory and are protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or reverse-engineer any part of the Service without our prior written consent.
						</Para>

						<SectionHeading id="payments">7. Payments & Subscriptions</SectionHeading>
						<BulletList items={[
							'Certain features of Eventory may require a paid subscription.',
							'Pricing and billing terms will be presented to you before purchase.',
							'Subscriptions renew automatically unless cancelled before the renewal date.',
							'Refunds are handled in accordance with our refund policy, available upon request.',
							'We reserve the right to change pricing with at least 30 days\' notice.',
						]} />

						<SectionHeading id="termination">8. Termination</SectionHeading>
						<Para>
							You may close your account at any time through your account settings or by contacting support. We may suspend or terminate your access if you violate these Terms, engage in fraudulent activity, or for any reason with reasonable notice.
						</Para>
						<Para>
							Upon termination, your right to use the Service ceases immediately. We may retain certain data as required by law or for legitimate business purposes, as described in our Privacy Policy.
						</Para>

						<SectionHeading id="disclaimers">9. Disclaimers</SectionHeading>
						<Para>
							THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
						</Para>

						<SectionHeading id="limitation">10. Limitation of Liability</SectionHeading>
						<Para>
							TO THE MAXIMUM EXTENT PERMITTED BY LAW, EVENTORY AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
						</Para>

						<SectionHeading id="indemnification">11. Indemnification</SectionHeading>
						<Para>
							You agree to indemnify, defend, and hold harmless Eventory and its affiliates from any claims, damages, losses, or expenses (including reasonable attorneys' fees) arising from your use of the Service, your violation of these Terms, or your violation of any third-party rights.
						</Para>

						<SectionHeading id="governing-law">12. Governing Law</SectionHeading>
						<Para>
							These Terms shall be governed by and construed in accordance with the laws of the Federal Republic of Nigeria, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be resolved in the competent courts of Lagos, Nigeria.
						</Para>

						<SectionHeading id="changes">13. Changes to Terms</SectionHeading>
						<Para>
							We may revise these Terms at any time by posting an updated version on the Eventory platform. Material changes will be communicated via email or an in-app notification at least 14 days before they take effect. Your continued use of the Service after such changes constitutes acceptance of the revised Terms.
						</Para>

						<SectionHeading id="contact">14. Contact Us</SectionHeading>
						<Para>
							If you have questions about these Terms of Service, please reach out:
						</Para>
						<Box
							bg="#F9FBFA"
							borderRadius="2xl"
							p={6}
							border="1px solid"
							borderColor="rgba(217, 219, 233, 0.6)"
							mb={4}
						>
							<Text fontSize="md" color="#4B5563" lineHeight="1.75" mb={1}><strong>Email:</strong> jason@geteventory.app</Text>
							<Text fontSize="md" color="#4B5563" lineHeight="1.75"><strong>Address:</strong> Stratospia Limited, Lagos, Nigeria</Text>
						</Box>
	</>
);

const TermsPage = () => {
	return (
		<Box w="100%" minH="100vh" bg="white" fontFamily="'DM Sans', sans-serif">
			{/* Header */}
			<Box bg="#F2F7F8" pt={{ base: '100px', md: '120px' }} pb={{ base: 12, md: 16 }}>
				<Container maxW="container.lg">
					<VStack spacing={4} textAlign="center">
						<Heading
							as="h1"
							fontSize={{ base: '4xl', md: '52px' }}
							fontFamily="'Poppins', sans-serif"
							fontWeight="700"
							color="#111113"
							lineHeight="1.15"
						>
							Terms of Service
						</Heading>
						<Text fontSize={{ base: 'md', md: 'xl' }} color="#6B7280" maxW="2xl">
							Please read these terms carefully before using Eventory. By accessing or using our platform, you agree to be bound by these terms.
						</Text>
						<Text fontSize="sm" color="#9CA3AF" mt={2}>
							Last updated: October 9, 2026
						</Text>
					</VStack>
				</Container>
			</Box>

			{/* Content */}
			<Container maxW="container.lg" py={{ base: 10, md: 16 }}>
				<Flex direction={{ base: 'column', lg: 'row' }} gap={{ base: 8, lg: 16 }}>
					{/* Sidebar TOC */}
					<Box
						as="nav"
						w={{ base: '100%', lg: '280px' }}
					flexShrink={0}
					position={{ base: 'static', lg: 'sticky' }}
					top="100px"
					alignSelf="flex-start"
				>
						<Box
							bg="#F9FBFA"
							borderRadius="2xl"
							p={6}
							border="1px solid"
							borderColor="rgba(217, 219, 233, 0.6)"
						>
							<Text fontSize="sm" fontWeight="700" color="#111113" mb={4} textTransform="uppercase" letterSpacing="0.05em">
								Contents
							</Text>
							<VStack align="flex-start" spacing={2}>
								{SECTIONS.map((s) => (
									<Text
										key={s.id}
										as="a"
										href={`#${s.id}`}
										fontSize="sm"
										color="#6B7280"
										_hover={{ color: '#FB8601' }}
										transition="color 0.15s"
										cursor="pointer"
									>
										{s.title}
									</Text>
								))}
							</VStack>
						</Box>
					</Box>

					{/* Main content */}
					<Box flex={1} minW={0}>
						<TermsContent />
					</Box>
				</Flex>
			</Container>

			{/* Footer */}
			<Box bg="#F2F7F8" py={8}>
				<Container maxW="container.xl">
					<Flex
						direction={{ base: 'column', md: 'row' }}
						justify="space-between"
						align="center"
						gap={4}
					>
						<HStack spacing={6}>
							<Text
								as={RouterLink}
								to="/terms"
								fontSize="sm"
								fontWeight="600"
								color="#FB8601"
								_hover={{ textDecoration: 'underline' }}
							>
								Terms of Service
							</Text>
							<Text
								as={RouterLink}
								to="/privacy"
								fontSize="sm"
								color="#6B7280"
								_hover={{ color: '#FB8601' }}
							>
								Privacy Policy
							</Text>
							<Text
								as={RouterLink}
								to="/"
								fontSize="sm"
								color="#6B7280"
								_hover={{ color: '#FB8601' }}
							>
								Home
							</Text>
						</HStack>
						<Text color="#9E9E9E" fontSize="sm" fontWeight="500">
							© 2026 Stratospia Limited. All rights reserved.
						</Text>
					</Flex>
				</Container>
			</Box>
		</Box>
	);
};

export default TermsPage;
